<template>
  <div class="relative">
    <div class="w-full h-125 md:h-150 rounded-2xl overflow-hidden">
      <ClientOnly>
        <div
          ref="mapEl"
          class="w-full h-full bg-gray-100"
        />
        <template #fallback>
          <div class="w-full h-full bg-gray-100" />
        </template>
      </ClientOnly>
    </div>

    <!-- Popup positioned relative to container, outside overflow:hidden -->
    <div
      v-show="openedMarker.active"
      ref="popupEl"
      class="absolute top-0 left-0 z-3 text-base w-70"
    >
      <CardRv
        v-if="openedMarker.type === 'rv' && openedMarker.rv"
        :key="openedMarker.rv.Id"
        :rv="openedMarker.rv"
        :search-request-id="''"
        :page-size="rvs?.length"
        cta="map"
        discovery-source="MapCard"
        has-close-button
        variant="map"
        is-seo-variant
        @close="closeCard"
      />

      <CampgroundsCard
        v-else-if="openedMarker.type === 'campground' && openedMarker.campground"
        :key="openedMarker.campground.Id"
        :campground="openedMarker.campground"
        no-lazy-load
        variant="map"
        has-close-button
        @close="closeCard"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useElementVisibility } from '@vueuse/core'
import { autoPlacement, computePosition, offset } from '@floating-ui/vue'
import { icon } from '@fortawesome/fontawesome-svg-core'
import type { RvUnifiedSearch } from '~/types/rv'
import type { Nullable } from '~/types'
import type { Campground, SeoEvent } from '~/types/rental-api-aliases'

const props = withDefaults(defineProps<{
  title?: string
  center?: {
    lat: number
    lng: number
  }
  bounds?: {
    north?: number
    south?: number
    east?: number
    west?: number
  }
  zoom?: number
  rvs?: RvUnifiedSearch[]
  campgrounds?: Campground[]
  events?: SeoEvent[]
}>(), {
  zoom: 8,
})

const markerClasses = 'w-5 h-5 pointer-events-none'
const rvMarkerSvg = icon({ iconName: 'rv', prefix: 'fal' }, { classes: markerClasses })?.html[0]
const campgroundMarkerSvg = icon({ iconName: 'campground', prefix: 'fal' }, { classes: markerClasses })?.html[0]
const eventMarkerSvg = icon({ iconName: 'music', prefix: 'fal' }, { classes: markerClasses })?.html[0]

const MARKER_BASE_CLASSES = 'cursor-pointer pointer-events-auto flex items-center justify-center w-10 h-10 rounded-full shadow-md text-white transition-transform hover:scale-110' as const

const { isMobileOrTablet } = useDevice()

const mapEl = useTemplateRef('mapEl')
const popupEl = useTemplateRef('popupEl')

const map = ref<Nullable<google.maps.Map>>(null)

// Use shallowRef for markers array - no deep reactivity needed
const markers = shallowRef<google.maps.marker.AdvancedMarkerElement[]>([])

// Store cleanup functions for marker event listeners
const markerCleanupFns = shallowRef<Array<() => void>>([])

const openedMarker = ref<{
  active: boolean
  type: Nullable<'campground' | 'rv'>
  rv: Nullable<RvUnifiedSearch>
  campground: Nullable<Campground>
  event: Nullable<{
    Id: string
    Name: string
    Latitude: number
    Longitude: number
  }>
}>({
  active: false,
  type: null,
  rv: null,
  campground: null,
  event: null,
})

function createMarkerContent(type: 'campground' | 'event' | 'rv'): HTMLElement {
  const el = document.createElement('div')
  let colorClass: string
  let icon: string
  switch (type) {
    case 'rv':
      colorClass = 'bg-primary-950'
      icon = rvMarkerSvg ?? ''
      break
    case 'campground':
      colorClass = 'bg-green-800'
      icon = campgroundMarkerSvg ?? ''
      break
    case 'event':
      colorClass = 'bg-orange-500'
      icon = eventMarkerSvg ?? ''
      break
    default:
      colorClass = 'bg-gray-800'
      icon = ''
      break
  }

  el.className = `${MARKER_BASE_CLASSES} ${colorClass}`
  el.innerHTML = icon

  return el
}

async function initMap() {
  const googleMaps = await useGoogleMaps()
  if (!googleMaps || !mapEl.value) return

  map.value = googleMaps.map(mapEl.value, {
    mapId: '3ca56b12845f4169',
    center: props.center,
    zoom: props.zoom,
    maxZoom: 16,
    minZoom: 4,
    disableDefaultUI: true,
    clickableIcons: false,
    gestureHandling: 'auto',
  })

  if (props.bounds && props.bounds.south !== undefined && props.bounds.west !== undefined && props.bounds.north !== undefined && props.bounds.east !== undefined) {
    map.value.fitBounds(
      new window.google.maps.LatLngBounds(
        { lat: props.bounds.south, lng: props.bounds.west },
        { lat: props.bounds.north, lng: props.bounds.east },
      ),
      0,
    )
  }

  // Close popup on any map interaction
  map.value.addListener('click', closeCard)
  map.value.addListener('dragstart', closeCard)
  map.value.addListener('zoom_changed', closeCard)

  await populateMarkers()
}

function clearMarkers() {
  // Run all cleanup functions to remove event listeners
  markerCleanupFns.value.forEach((cleanup) => cleanup())
  markerCleanupFns.value = []

  // Remove markers from map
  markers.value.forEach((marker) => {
    google.maps.event.clearInstanceListeners(marker)
    marker.map = null
  })
  markers.value = []
}

async function populateMarkers() {
  const googleMaps = await useGoogleMaps()
  if (!map.value || !googleMaps) return

  // Clear existing markers with proper cleanup
  clearMarkers()

  const newMarkers: google.maps.marker.AdvancedMarkerElement[] = []
  const newCleanupFns: (() => void)[] = []

  // Add RV markers
  props.rvs?.forEach((rv) => {
    if (!rv.Latitude || !rv.Longitude) return

    const el = createMarkerContent('rv')
    const marker = googleMaps.advancedMarker({
      map: toRaw(map.value!),
      position: { lat: rv.Latitude, lng: rv.Longitude },
      content: el,
      zIndex: 1,
    })

    // Store event handlers for cleanup
    function handleMouseEnter() {
      marker.zIndex = 2
    }
    function handleMouseLeave() {
      if (openedMarker.value.rv?.Id !== rv.Id) {
        marker.zIndex = 1
      }
    }

    marker.addListener('gmp-click', () => {
      const markerContent = marker.content as HTMLElement
      if (markerContent) openRvCard(rv, markerContent)
    })
    el.addEventListener('mouseenter', handleMouseEnter)
    el.addEventListener('mouseleave', handleMouseLeave)

    // Store cleanup function
    newCleanupFns.push(() => {
      el.removeEventListener('mouseenter', handleMouseEnter)
      el.removeEventListener('mouseleave', handleMouseLeave)
    })

    newMarkers.push(marker)
  })

  // Add Campground markers
  props.campgrounds?.forEach((campground) => {
    if (!campground.Latitude || !campground.Longitude) return

    const el = createMarkerContent('campground')
    const marker = googleMaps.advancedMarker({
      map: toRaw(map.value!),
      position: { lat: campground.Latitude, lng: campground.Longitude },
      content: el,
      zIndex: 1,
    })

    // Store event handlers for cleanup
    function handleMouseEnter() {
      marker.zIndex = 2
    }
    function handleMouseLeave() {
      if (openedMarker.value.campground?.Id !== campground.Id) {
        marker.zIndex = 1
      }
    }

    marker.addListener('gmp-click', () => {
      const markerContent = marker.content as HTMLElement
      if (markerContent) openCampgroundCard(campground, markerContent)
    })
    el.addEventListener('mouseenter', handleMouseEnter)
    el.addEventListener('mouseleave', handleMouseLeave)

    // // Store cleanup function
    newCleanupFns.push(() => {
      el.removeEventListener('mouseenter', handleMouseEnter)
      el.removeEventListener('mouseleave', handleMouseLeave)
    })

    newMarkers.push(marker)
  })

  // Add Event markers
  props.events?.forEach((event) => {
    if (!event.Latitude || !event.Longitude) return

    const el = createMarkerContent('event')
    const marker = googleMaps.advancedMarker({
      map: toRaw(map.value!),
      position: { lat: event.Latitude, lng: event.Longitude },
      content: el,
      zIndex: 1,
    })

    // Store event handlers for cleanup
    function handleMouseEnter() {
      marker.zIndex = 2
    }
    function handleMouseLeave() {
      if (openedMarker.value.event?.Id !== event.Id) {
        marker.zIndex = 1
      }
    }

    el.addEventListener('mouseenter', handleMouseEnter)
    el.addEventListener('mouseleave', handleMouseLeave)

    // Store cleanup function
    newCleanupFns.push(() => {
      el.removeEventListener('mouseenter', handleMouseEnter)
      el.removeEventListener('mouseleave', handleMouseLeave)
    })

    newMarkers.push(marker)
  })

  markers.value = newMarkers
  markerCleanupFns.value = newCleanupFns
}

function openRvCard(rv: RvUnifiedSearch, markerEl: HTMLElement) {
  openedMarker.value.active = false
  openedMarker.value.type = 'rv'
  openedMarker.value.rv = rv
  openedMarker.value.campground = null

  positionPopup(markerEl)
  openedMarker.value.active = true
}

function openCampgroundCard(campground: Campground, markerEl: HTMLElement) {
  openedMarker.value.active = false
  openedMarker.value.type = 'campground'
  openedMarker.value.campground = campground
  openedMarker.value.rv = null

  positionPopup(markerEl)
  openedMarker.value.active = true
}

function positionPopup(markerEl: HTMLElement) {
  if (!popupEl.value || !mapEl.value) return

  // Clear previous position styles (same pattern as search map)
  popupEl.value.style.removeProperty('left')
  popupEl.value.style.removeProperty('top')
  popupEl.value.style.removeProperty('transform')

  if (isMobileOrTablet) {
    popupEl.value.style.setProperty('left', '50%')
    popupEl.value.style.setProperty('top', '50%')
    popupEl.value.style.setProperty('transform', 'translate(-50%, -50%)')
  }
  else {
    // Use floating-ui same pattern as search map
    nextTick(() => {
      computePosition(markerEl, popupEl.value as HTMLElement, {
        placement: 'bottom',
        middleware: [offset(4), autoPlacement({ padding: 16, boundary: mapEl.value as Element })],
      }).then(({ x, y }) => {
        popupEl.value?.style.setProperty('left', `${x}px`)
        popupEl.value?.style.setProperty('top', `${y}px`)
        popupEl.value?.style.setProperty('transform', '')
      })
    })
  }
}

function closeCard() {
  openedMarker.value.active = false
}

// Lazy-initialize map only when visible in viewport
const isMapVisible = useElementVisibility(mapEl, {
  once: true,
})

watch(isMapVisible, (visible) => {
  if (visible) initMap()
}, { once: true })

onUnmounted(() => {
  // Clean up all markers and their event listeners
  clearMarkers()

  // Clean up map listeners
  if (map.value) {
    google.maps.event.clearInstanceListeners(map.value)
    map.value = null
  }
})
</script>
